import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from "mobx";
import { toast } from "react-toastify";

import agent from "../api/agent";

export default class CompoundStore {
  rootStore;

  displayLoading = false;
  compoundRegistry = new Map();
  compoundRegistryExpanded = new Map();

  cacheValid = false;

  selectedCompound = null;

  loadingFetchCompound = false;
  editingCompound = false;

  constructor(rootStore) {
    this.rootStore = rootStore;
    makeObservable(this, {
      displayLoading: observable,
      cacheValid: observable,

      fetchCompounds: action,
      compounds: computed,
      compoundRegistry: observable,

      selectedCompound: observable,

      loadingFetchCompound: observable,
      fetchCompound: action,
      compoundRegistryExpanded: observable,

      loadingProjectCompounds: observable,
      fetchCompoundsByProject: action,
      editingCompound: observable,
      editCompound: action,
      editCompoundExternalId: action,

      compoundDatasets: observable,
      loadingDatasets: observable,
      fetchDataSets: action,

      // ── Custom Columns ──
      compoundColumns: observable,
      loadingColumns: observable,
      fetchColumns: action,
      saveColumns: action,
    });
  }

  fetchCompounds = async () => {
    this.displayLoading = true;

    if (this.cacheValid) {
      this.displayLoading = false;
      return;
    }

    try {
      let fetchedCompounds = await agent.Compounds.list();
      runInAction(() => {
        fetchedCompounds.forEach((compound) => {
          this.compoundRegistry.set(compound.id, compound);
        });
        this.displayLoading = false;
        this.cacheValid = true;
      });
    } catch (error) {
      runInAction(() => {
        this.displayLoading = false;
      });
      toast.error("Problem loading compounds");
      console.log(error);
    }
  };

  loadingProjectCompounds = false;
  projectCompounds = [];

  // ── DataSets ──
  compoundDatasets = [];
  savedFilters = {};
  loadingDatasets = false;

  // ── Custom Columns ──
  compoundColumns = [];
  loadingColumns = false;

  fetchCompoundsByProject = async (projectId) => {
    this.loadingProjectCompounds = true;
    try {
      let fetched = await agent.Compounds.listByProject(projectId);
      runInAction(() => {
        this.projectCompounds = fetched;
        this.loadingProjectCompounds = false;
      });
    } catch (error) {
      runInAction(() => {
        this.loadingProjectCompounds = false;
      });
      toast.error("Problem loading project compounds");
      console.log(error);
    }
  };

  fetchColumns = async (projectId) => {
    this.loadingColumns = true;
    try {
      const cols = await agent.Compounds.listColumns(projectId);
      runInAction(() => {
        this.compoundColumns = cols;
        this.loadingColumns = false;
      });
    } catch (error) {
      runInAction(() => { this.loadingColumns = false; });
      console.error("Failed to load compound columns", error);
    }
  };

  saveColumns = async (projectId, columns) => {
    try {
      const result = await agent.Compounds.saveColumns(projectId, { columns });
      runInAction(() => {
        this.compoundColumns = result;
      });
      return result;
    } catch (error) {
      console.error("Failed to save columns", error);
      return null;
    }
  };

  get compounds() {
    return Array.from(this.compoundRegistry.values());
  }

  fetchCompound = async (id) => {
    this.loadingFetchCompound = true;

    // first check cache
    let fetchedCompound = this.compoundRegistryExpanded.get(id);
    if (this.cacheValid && fetchedCompound) {
      this.selectedCompound = fetchedCompound;
      this.loadingFetchCompound = false;
    }
    // if not found fetch from api
    else {
      try {
        fetchedCompound = await agent.Compounds.details(id);
        runInAction(() => {
          this.selectedCompound = fetchedCompound;
          this.compoundRegistryExpanded.set(id, fetchedCompound);
        });
      } catch (error) {
        console.error(error);
      } finally {
        runInAction(() => {
          this.loadingFetchCompound = false;
        });
      }
    }
  };

  editCompound = async (compound) => {
    this.editingCompound = true;
    let res = null;
    // send to server
    try {
      res = await agent.Compounds.edit(this.selectedCompound.id, compound);
      runInAction(() => {
        this.cacheValid = false;
        this.fetchCompound(this.selectedCompound.id);
      });
    } catch (error) {
      console.error(error);
    } finally {
      runInAction(() => {
        this.editingCompound = false;
      });
    }
    return res;
  };

  editCompoundExternalId = async (compound) => {
    this.editingCompound = true;
    let res = null;
    // send to server
    try {
      res = await agent.Compounds.editExternalId(
        this.selectedCompound.id,
        compound
      );
      runInAction(() => {
        this.cacheValid = false;
        this.fetchCompound(this.selectedCompound.id);
      });
    } catch (error) {
      console.error(error);
    } finally {
      runInAction(() => {
        this.editingCompound = false;
      });
    }
    return res;
  };
  addCompoundActivity = async (compoundId, activity) => {
    this.editingCompound = true;
    let res = null;
    try {
      res = await agent.Compounds.addActivity(compoundId, activity);
      runInAction(() => {
        this.cacheValid = false;
      });
    } catch (error) {
      console.error(error);
    } finally {
      runInAction(() => {
        this.editingCompound = false;
      });
    }
    return res;
  };

  editCompoundActivity = async (activityId, activity) => {
    this.editingCompound = true;
    let res = null;
    try {
      res = await agent.Compounds.editActivity(activityId, activity);
      runInAction(() => {
        this.cacheValid = false;
      });
    } catch (error) {
      console.error(error);
    } finally {
      runInAction(() => {
        this.editingCompound = false;
      });
    }
    return res;
  };

  deleteCompoundActivity = async (activityId) => {
    this.editingCompound = true;
    let res = null;
    try {
      res = await agent.Compounds.deleteActivity(activityId);
      runInAction(() => {
        this.cacheValid = false;
      });
    } catch (error) {
      console.error(error);
    } finally {
      runInAction(() => {
        this.editingCompound = false;
      });
    }
    return res;
  };

  deleteCompound = async (id) => {
    this.editingCompound = true;
    let res = null;
    try {
      res = await agent.Compounds.delete(id);
      runInAction(() => {
        this.cacheValid = false;
      });
    } catch (error) {
      console.error(error);
    } finally {
      runInAction(() => {
        this.editingCompound = false;
      });
    }
    return res;
  };

  batchDeleteCompounds = async (ids) => {
    this.editingCompound = true;
    let res = null;
    try {
      res = await agent.Compounds.batchDelete(ids);
      runInAction(() => {
        this.cacheValid = false;
      });
    } catch (error) {
      console.error(error);
    } finally {
      runInAction(() => {
        this.editingCompound = false;
      });
    }
    return res;
  };

  // ── DataSet actions ──

  fetchDataSets = async (projectId) => {
    this.loadingDatasets = true;
    try {
      const sets = await agent.Compounds.listDataSets(projectId);
      runInAction(() => {
        this.compoundDatasets = sets || [];
        this.loadingDatasets = false;
      });
    } catch (error) {
      runInAction(() => { this.loadingDatasets = false; });
    }
  };

  createDataSet = async (body) => {
    const res = await agent.Compounds.createDataSet(body);
    if (res) {
      runInAction(() => {
        this.compoundDatasets.push(res);
      });
    }
    return res;
  };

  deleteDataSet = async (id) => {
    await agent.Compounds.deleteDataSet(id);
    runInAction(() => {
      this.compoundDatasets = this.compoundDatasets.filter((d) => d.id !== id);
    });
  };
}
